import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-multi-date-picker';
import TimePicker from 'react-multi-date-picker/plugins/time_picker';
import Toolbar from 'react-multi-date-picker/plugins/toolbar';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { CloseCircleOutlined } from '@ant-design/icons';
import { getPersianHolidaysMap } from '../utils/persianHolidaysCache';
import { FRIDAY_LABEL, isPersianFriday } from '../utils/persianHolidayDates';

const toLatinDigits = (value) => {
  if (value == null) return '';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  let result = String(value);
  for (let i = 0; i < 10; i += 1) {
    result = result.replace(new RegExp(persianDigits[i], 'g'), String(i));
    result = result.replace(new RegExp(arabicDigits[i], 'g'), String(i));
  }
  return result;
};

const normalizeDateKey = (value) => toLatinDigits(value).replace(/\//g, '-');

const HOLIDAY_STYLE = {
  color: '#dc2626',
  backgroundColor: '#fff1f2',
  borderRadius: '8px',
  fontWeight: 700,
};

const FRIDAY_STYLE = {
  color: '#b45309',
  backgroundColor: '#fffbeb',
  borderRadius: '8px',
  fontWeight: 600,
};

const PersianDatePicker = ({
  value,
  onChange,
  placeholder = 'انتخاب تاریخ',
  range = false,
  multiple = false,
  showTime = false,
  style,
  allowClear = true,
  highlightHolidays = true,
  mapDays,
  ...props
}) => {
  const [holidayMap, setHolidayMap] = useState(() => new Map());

  useEffect(() => {
    if (!highlightHolidays) return;
    let active = true;
    getPersianHolidaysMap().then((map) => {
      if (active) setHolidayMap(map);
    });
    return () => {
      active = false;
    };
  }, [highlightHolidays]);

  const hasValue = useMemo(() => {
    if (value == null || value === '') return false;
    if (range) {
      if (Array.isArray(value)) return value.some((v) => v != null && v !== '');
      if (typeof value === 'object' && (value.start || value.end)) return true;
      return false;
    }
    return true;
  }, [value, range]);

  const handleClear = (e) => {
    e.stopPropagation();
    if (onChange) {
      onChange(range ? [] : null);
    }
  };

  const plugins = [
    ...(allowClear ? [<Toolbar key="toolbar" sort={['today', 'deselect', 'close']} />] : []),
    ...(showTime ? [<TimePicker key="time-picker" position="bottom" />] : []),
  ].filter(Boolean);
  const format = showTime ? 'YYYY/MM/DD HH:mm' : 'YYYY/MM/DD';

  return (
    <div className="persian-date-picker-wrapper" style={{ position: 'relative', width: '100%' }}>
      <DatePicker
        calendar={persian}
        locale={persian_fa}
        format={format}
        placeholder={placeholder}
        range={range}
        multiple={multiple}
        editable={false}
        plugins={plugins}
        containerStyle={{ width: '100%' }}
        style={{ width: '100%', height: '32px', paddingLeft: allowClear && hasValue ? 28 : undefined, ...style }}
        value={value}
        onChange={onChange}
        mapDays={(args) => {
          const external = typeof mapDays === 'function' ? mapDays(args) : {};
          if (!highlightHolidays) return external;

          const key = normalizeDateKey(args.date?.format?.('YYYY-MM-DD'));
          const occasion = holidayMap.get(key);
          const friday = isPersianFriday(args.date);

          if (!occasion && !friday) return external;

          const title = friday && occasion
            ? `${FRIDAY_LABEL} — ${occasion}`
            : friday
              ? FRIDAY_LABEL
              : occasion;

          const classNames = [
            external?.className,
            friday ? 'persian-date-friday' : '',
            occasion ? 'persian-date-holiday' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return {
            ...external,
            className: classNames,
            title,
            style: {
              ...(external?.style || {}),
              ...(occasion ? HOLIDAY_STYLE : {}),
              ...(friday && !occasion ? FRIDAY_STYLE : {}),
            },
          };
        }}
        {...props}
      />
      {allowClear && hasValue && (
        <button
          type="button"
          onClick={handleClear}
          className="persian-date-picker-clear"
          aria-label="پاک کردن تاریخ"
          style={{
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: '#999',
            fontSize: 14,
            lineHeight: 1,
            zIndex: 2,
          }}
        >
          <CloseCircleOutlined style={{ fontSize: 16 }} />
        </button>
      )}
      <style>{`
        .persian-date-holiday {
          color: #dc2626 !important;
          background: #fff1f2 !important;
          border-radius: 8px !important;
          font-weight: 700 !important;
        }
        .persian-date-friday {
          color: #b45309 !important;
          background: #fffbeb !important;
          border-radius: 8px !important;
          font-weight: 600 !important;
        }
      `}</style>
    </div>
  );
};

export default PersianDatePicker;
